import { ReactNode } from 'react'
import {
	Bars3Icon,
	CalendarDaysIcon,
	CreditCardIcon,
	MapPinIcon,
	UserIcon
} from '@heroicons/react/24/outline'
import { addDays } from 'libs/dates'
import { Controller, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { DatePicker, FormSection, Select, Textarea, UserSelect } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useSelect } from 'shared/hooks'
import { cn } from 'utils/style'
import { usePaymentMethod } from '../../pages/create/hooks/usePaymentMethod'
import { usePaymentStatus } from '../../pages/create/hooks/usePaymentStatus'
import type { useBookingOptions } from '../../hooks/useBookingOptions'

type BookingFormSectionsProps = {
	form: UseFormReturn<FieldValues>
	options: ReturnType<typeof useBookingOptions>
	mode: 'create' | 'edit'
	showUser?: boolean
	showNotes?: boolean
	disabled?: boolean
	periodDisabled?: boolean
	notesDisabled?: boolean
	minStartDate?: Date
	notesAside?: ReactNode
}

export const BookingFormSections = ({
	form,
	options,
	mode,
	showUser = true,
	showNotes = true,
	disabled = false,
	periodDisabled = disabled,
	notesDisabled = disabled,
	minStartDate,
	notesAside
}: BookingFormSectionsProps) => {
	const { t } = useTranslation()
	const paymentStatus = usePaymentStatus()
	const paymentMethod = usePaymentMethod()
	const {
		register,
		control,
		setValue,
		trigger,
		watch,
		formState: { errors }
	} = form
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const fieldErrors = errors as any
	const watchFrom = watch('from')
	const isEdit = mode === 'edit'
	const alignRows = isEdit ? 'md' : 'lg'

	const selectChange = (id: string) => (data: any) =>
		handleSelectChange({ id, value: data?.value, label: data?.label })

	return (
		<>
			<div className={cn('grid gap-5', isEdit ? 'md:grid-cols-2' : 'lg:grid-cols-2')}>
				<FormSection
					title={t('User information')}
					description={t('Who is making this booking and what they are renting.')}
					icon={<UserIcon />}
					alignRows={alignRows}
					className={showUser ? undefined : 'sm:grid-cols-1'}>
					{showUser && (
						<Controller
							name="user"
							control={control}
							render={({ field }) => (
								<UserSelect
									id="user"
									variant="form"
									isClearable={false}
									label={t('User')}
									placeholder={t('Default')}
									required
									error={fieldErrors.user?.value}
									{...field}
									disabled={disabled}
									onChange={selectChange('user')}
								/>
							)}
						/>
					)}
					<Controller
						name="rental"
						control={control}
						render={({ field }) => (
							<Select
								id="rental"
								variant="form"
								isClearable={false}
								placeholder={t('Default')}
								label={t('Rental')}
								required
								options={options.rentals.options}
								handleInfiniteScroll={options.rentals.fetchNextPage}
								isLoading={options.rentals.isLoading}
								{...field}
								error={fieldErrors.rental?.value}
								isDisabled={disabled}
								onChange={selectChange('rental')}
							/>
						)}
					/>
				</FormSection>

				<FormSection
					title={t('Rental')}
					description={t('Area and total commission.')}
					icon={<MapPinIcon />}
					alignRows={alignRows}
					tone="warning">
					<Controller
						name="booth"
						control={control}
						render={({ field }) => (
							<Select
								id="booth"
								variant="form"
								isClearable={false}
								placeholder={t('Default')}
								label={isEdit ? t('Area') : t('Area')}
								required
								options={options.booths.options}
								handleInfiniteScroll={options.booths.fetchNextPage}
								isLoading={options.booths.isLoading}
								{...field}
								error={fieldErrors.booth?.value}
								isDisabled={disabled}
								onChange={selectChange('booth')}
							/>
						)}
					/>
					<InputGroup
						id="commission"
						type="number"
						step="0.01"
						placeholder="0"
						suffix="%"
						label={isEdit ? t('Commission') : t('Total commission')}
						required
						disabled={disabled}
						{...register('commission', {
							required: t('Commission is required'),
							validate: (value) => Number(value) >= 0 || t('Commission cannot be negative')
						})}
						error={fieldErrors.commission}
					/>
				</FormSection>

				<FormSection
					title={t('Period')}
					description={t('Start and end date of the booking.')}
					icon={<CalendarDaysIcon />}
					alignRows={alignRows}
					tone="success">
					<Controller
						control={control}
						name="from"
						rules={{ required: isEdit && t('Start date is required') }}
						render={({ field }) => (
							<DatePicker
								label={t('From')}
								required={isEdit}
								onChange={field.onChange}
								selected={field.value}
								minDate={minStartDate}
								disabled={periodDisabled}
								error={fieldErrors.from}
							/>
						)}
					/>
					<Controller
						control={control}
						name="to"
						rules={{ required: isEdit && t('End date is required') }}
						render={({ field }) => (
							<DatePicker
								label={t('To')}
								required={isEdit}
								onChange={field.onChange}
								selected={field.value}
								minDate={watchFrom ? addDays(new Date(watchFrom), 1) : minStartDate}
								disabled={periodDisabled}
								error={fieldErrors.to}
							/>
						)}
					/>
				</FormSection>

				<FormSection
					title={t('Payment')}
					description={t('Payment status and method.')}
					icon={<CreditCardIcon />}
					alignRows={alignRows}
					tone="danger">
					<Controller
						name="payment.status"
						control={control}
						render={({ field }) => (
							<Select
								id="payment.status"
								variant="form"
								isClearable={false}
								placeholder={t('Default')}
								label={isEdit ? t('Payment status') : t('Status')}
								options={paymentStatus}
								{...field}
								isDisabled={disabled}
							/>
						)}
					/>
					<Controller
						name="payment.method"
						control={control}
						render={({ field }) => (
							<Select
								id="payment.method"
								variant="form"
								isClearable={false}
								placeholder={t('Default')}
								label={isEdit ? t('Payment method') : t('Method')}
								options={paymentMethod}
								{...field}
								isDisabled={disabled}
							/>
						)}
					/>
				</FormSection>
			</div>

			{showNotes && (
				<FormSection
					title={t('Additional information')}
					description={t('Internal notes for this booking.')}
					icon={<Bars3Icon />}
					className="sm:grid-cols-1">
					{notesAside}
					<Textarea
						id="store_notes"
						label={t('Notes')}
						placeholder={t('Optional notes…')}
						disabled={notesDisabled}
						{...register('store_notes')}
					/>
				</FormSection>
			)}
		</>
	)
}
